import { createContext, useContext, useEffect, useRef } from "react";

export interface Breadcrumb {
  /** Display label. Use "…" while loading a dynamic value. */
  label: string;
  /** Omit on the last crumb (the current page) or whenever the crumb
   *  shouldn't link (e.g. a parent grouping like "Admin"). */
  href?: string;
}

/**
 * Metadata a route or page can declare about itself. Static meta lives in
 * the route registry (lib/routes.tsx); components can override parts of it
 * at runtime via `usePageMeta` (useful for async-loaded dynamic titles).
 */
export interface PageMeta {
  /** Text for document.title. If a crumbs list is provided without a title,
   *  the last crumb's label is used instead. */
  title?: string;
  /** Rendered left-to-right in the topbar, joined with "›" separators. */
  breadcrumbs?: Breadcrumb[];
}

type Ctx = {
  /** The active page's runtime override, or null when no override is set. */
  override: PageMeta | null;
  setOverride: (meta: PageMeta | null) => void;
};

export const PageMetaContext = createContext<Ctx | null>(null);

/**
 * Per-page runtime override for metadata. Pages call this inside their
 * component when the title or breadcrumbs depend on async data (e.g. the
 * current customer's name). Only one override can be active at a time —
 * since only one page renders at a time, this is fine.
 *
 * Passing `null` or unmounting clears the override automatically, restoring
 * the route registry's static meta.
 */
export function usePageMeta(meta: PageMeta | null) {
  const ctx = useContext(PageMetaContext);
  if (!ctx) {
    throw new Error("usePageMeta must be called inside <AppShell>");
  }
  const { setOverride } = ctx;

  // Serialize for dep comparison so inline object literals don't thrash.
  // Breadcrumb arrays are small; JSON.stringify cost is negligible.
  const key = meta ? JSON.stringify(meta) : null;

  // Mirror meta into a ref so the effect can read the latest reference
  // without listing the unstable `meta` literal in deps.
  const metaRef = useRef(meta);
  useEffect(() => {
    metaRef.current = meta;
  });

  useEffect(() => {
    setOverride(metaRef.current);
    return () => setOverride(null);
  }, [key, setOverride]);
}
